import { MUICheckBox, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { DeviceRolePost } from "../../../../models/DeviceRole"
import { ControllerRendererProps } from "../../../../types/reactHookForm"

type DeviceRoleBooleanFields = "isDefault" | "isAdmin" | "isOwner" | "isAssignable"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string(),
  isDefault: z.boolean({ error: "Is Default is required" }),
  isAdmin: z.boolean({ error: "Is Admin is required" }),
  isOwner: z.boolean({ error: "Is Owner is required" }),
  isAssignable: z.boolean({ error: "Is Assignable is required" }),
  maxOccurrences: z.number({ error: "Max Occurrences is required" }),
})

export function DeviceRoleCreate() {
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/devices/${deviceId}#roles`

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<DeviceRolePost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      name: "",
      description: "",
      isDefault: false,
      isAdmin: false,
      isOwner: false,
      isAssignable: false,
      maxOccurrences: 0,
    })
  }, [reset])

  const onSubmit = (values: DeviceRolePost) => digitalManagerApi
      .post(`/devices/${deviceId}/roles`, {
        name: values.name,
        description: values.description,
        isDefault: values.isDefault,
        isAdmin: values.isAdmin,
        isOwner: values.isOwner,
        isAssignable: values.isAssignable,
        maxOccurrences: values.maxOccurrences,
      })
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Role created")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot create role")
      })

  const handleParsedNumberChange = useCallback(
    (field: ControllerRenderProps<DeviceRolePost, "maxOccurrences">) =>
      (value: string) => {
        const parsed = value ? Number.parseInt(value, 10) : ""
        field.onChange(parsed)
      },
    []
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<DeviceRolePost, "name" | "description">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleBooleanToggle = useCallback(
    (field: ControllerRenderProps<DeviceRolePost, DeviceRoleBooleanFields>) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: ControllerRendererProps<DeviceRolePost, "name">) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const renderDescriptionTextField = useCallback(
    ({ field }: ControllerRendererProps<DeviceRolePost, "description">) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.description?.message === "string"
            ? { description: { message: errors.description.message } }
            : undefined
        }
      />
    ),
    [errors.description, handleTextFieldChange]
  )

  const renderIsDefaultCheckbox = useCallback(
    ({ field }: ControllerRendererProps<DeviceRolePost, "isDefault">) => (
      <MUICheckBox
        id="isDefault"
        checked={!!field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsAdminCheckbox = useCallback(
    ({ field }: ControllerRendererProps<DeviceRolePost, "isAdmin">) => (
      <MUICheckBox
        id="isAdmin"
        checked={!!field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsOwnerCheckbox = useCallback(
    ({ field }: ControllerRendererProps<DeviceRolePost, "isOwner">) => (
      <MUICheckBox
        id="isOwner"
        checked={!!field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderIsAssignableCheckbox = useCallback(
    ({ field }: ControllerRendererProps<DeviceRolePost, "isAssignable">) => (
      <MUICheckBox
        id="isAssignable"
        checked={!!field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderMaxOccurrencesTextField = useCallback(
    ({ field }: ControllerRendererProps<DeviceRolePost, "maxOccurrences">) => (
      <MUITextField
        id="maxOccurrences"
        type="number"
        size={32}
        name={field.name}
        value={
          field.value !== undefined && field.value !== null && !Number.isNaN(field.value)
            ? String(field.value)
            : ""
        }
        onChange={handleParsedNumberChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.maxOccurrences?.message === "string"
            ? { maxOccurrences: { message: errors.maxOccurrences.message } }
            : undefined
        }
      />
    ),
    [errors.maxOccurrences, handleParsedNumberChange]
  )
  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameTextField} />
      </FieldRow>
      <FieldRow label="Description">
        <Controller
          name="description"
          control={control}
          render={renderDescriptionTextField}
        />
      </FieldRow>
      <FieldRow label="Is Default">
        <Controller name="isDefault" control={control} render={renderIsDefaultCheckbox} />
      </FieldRow>
      <FieldRow label="Is Admin">
        <Controller name="isAdmin" control={control} render={renderIsAdminCheckbox} />
      </FieldRow>
      <FieldRow label="Is Owner">
        <Controller name="isOwner" control={control} render={renderIsOwnerCheckbox} />
      </FieldRow>
      <FieldRow label="Is Assignable">
        <Controller
          name="isAssignable"
          control={control}
          render={renderIsAssignableCheckbox}
        />
      </FieldRow>
      <FieldRow label="Max Occurrences">
        <Controller
          name="maxOccurrences"
          control={control}
          render={renderMaxOccurrencesTextField}
        />
      </FieldRow>
    </FormPage>
  )
}
